import { useFetcher } from "@remix-run/react";
import { z } from "zod";
import { getFormProps, getInputProps, getSelectProps, getTextareaProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { Button } from "~/components/ui/button";
import { Field, TextareaField, CheckboxField, SelectField } from '~/components/forms';
import { ExternalLink } from "lucide-react";
import { AuthenticityTokenInput } from "remix-utils/csrf/react";
import { useState } from "react";
import { PhoneCountrySelect } from "./phoneCountrySelect";

export const schema = z.object({
  message: z.string().min(1, "Por favor, escribe un mensaje"),
  email: z.string().email("Email inválido"),
  name: z.string().min(1, "Por favor, introduce tu nombre"),
  lastname: z.string().min(1, "Por favor, introduce tu apellido"),
  workspaceId: z.string(),
  category: z.string(),
  phoneNumber: z.string()
    .min(1, "El número de teléfono debe tener al menos 9 dígitos")
    .max(20, "El número de teléfono es demasiado largo")
    .optional(),
  phoneCountryCode: z.string()
    .min(1, "Selecciona el código de país")
    .optional(),
  consent: z.enum(['on', '']).optional(),
  preferredRentalSchedule: z.enum(['morning', 'afternoon', 'all-day'], {
    errorMap: () => ({ message: "Por favor, selecciona tu horario preferido" }),
  }),
  headcount: z.coerce.number().int().positive().optional(),
  timing: z.enum(['now', 'lt_3mo', 'exploring']).optional(),
});

export function ContactForm({ 
  workspaceId, isMobile, category,
}: { 
  workspaceId: string;
  priceMin?: number;
  priceMax?: number;
  isMobile?: boolean;
  category?: string;
}) {
  const [showContactFields, setShowContactFields] = useState(isMobile);
  const contactForm = useFetcher<{ success?: boolean; messageId?: string }>()
  const isSuccess = contactForm.data?.success;
  const isSubmitting = contactForm.state === 'submitting' || contactForm.state === 'loading';

  const [form, fields] = useForm({
    id: "contact_form",
    defaultValue: { workspaceId },
    constraint: getZodConstraint(schema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema });
    },
    shouldValidate: "onBlur",
  });

  return (
    <div className="space-y-4">
      <contactForm.Form
        method="POST"
        action="/resources/contact-workspace"
        {...getFormProps(form)}
      >
        <div className="space-y-4">
          <AuthenticityTokenInput />
          <input type="hidden" name="workspaceId" value={workspaceId} />
          <input type="hidden" name="category" value={category} />
          
          <TextareaField
            labelProps={{ htmlFor: fields.message.id, children: 'Pregunta al anunciate' }}
            textareaProps={{
              ...getTextareaProps(fields.message),
              className: 'min-h-32',
              disabled: isSuccess,
              ...(!isMobile && {
                onFocus: () => setShowContactFields(true),
              }),
            }}
            errors={fields.message.errors}
          />
          {showContactFields && !isSuccess && (
            <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
              <Field
                labelProps={{ htmlFor: fields.name.id, children: 'Nombre' }}
                inputProps={{
                  ...getInputProps(fields.name, {type: 'text'}),
                  placeholder: 'Tu nombre'
                }}
                errors={fields.name.errors}
              />
              
              <Field
                labelProps={{ htmlFor: fields.lastname.id, children: 'Apellido' }}
                inputProps={{
                  ...getInputProps(fields.lastname, {type: 'text'}),
                  placeholder: 'Tu apellido'
                }}
                errors={fields.lastname.errors}
              />
              
              <Field
                labelProps={{ htmlFor: fields.email.id, children: 'Email' }}
                inputProps={{
                  ...getInputProps(fields.email, {type: 'email', placeholder: 'tu@email.com'}),
                }}
                errors={fields.email.errors}
              />
               <div className="grid grid-cols-2 gap-4">
                <PhoneCountrySelect
                  labelProps={{
                    htmlFor: fields.phoneCountryCode.id,
                    children: 'País'
                  }}
                  selectProps={{
                    ...getSelectProps(fields.phoneCountryCode),
                    defaultValue: "+34-ES"
                  }}
                  errors={fields.phoneCountryCode.errors}
                />

                <Field
                  labelProps={{
                    htmlFor: fields.phoneNumber.id,
                    children: 'Teléfono'
                  }}
                  inputProps={{
                    ...getInputProps(fields.phoneNumber, { type: 'tel' }),
                    placeholder: '600 000 000',
                    className: 'w-full'
                  }}
                  errors={fields.phoneNumber.errors}
                />
              </div>

              <SelectField
                labelProps={{
                  htmlFor: fields.preferredRentalSchedule.id,
                  children: '¿En qué horario te gustaría alquilar el espacio?'
                }}
                selectProps={{
                  name: fields.preferredRentalSchedule.name,
                  defaultValue: fields.preferredRentalSchedule.initialValue as string,
                  disabled: isSuccess,
                  placeholder: 'Selecciona tu horario preferido',
                }}
                options={[
                  { value: 'morning', label: 'Mañana' },
                  { value: 'afternoon', label: 'Tarde' },
                  { value: 'all-day', label: 'Todo el día' },
                ]}
                errors={fields.preferredRentalSchedule.errors}
              />

              <Field
                labelProps={{ htmlFor: fields.headcount.id, children: '¿Para cuántas personas? (opcional)' }}
                inputProps={{
                  ...getInputProps(fields.headcount, { type: 'number' }),
                  min: 1,
                  disabled: isSuccess,
                }}
                errors={fields.headcount.errors}
              />

              <SelectField
                labelProps={{
                  htmlFor: fields.timing.id,
                  children: '¿Cuándo lo necesitas? (opcional)'
                }}
                selectProps={{
                  name: fields.timing.name,
                  defaultValue: fields.timing.initialValue as string,
                  disabled: isSuccess,
                  placeholder: 'Selecciona una opción',
                }}
                options={[
                  { value: 'now', label: 'Ahora' },
                  { value: 'lt_3mo', label: 'En menos de 3 meses' },
                  { value: 'exploring', label: 'Explorando' },
                ]}
                errors={fields.timing.errors}
              />

              <CheckboxField
                labelProps={{
                  children: (
                    <>
                      Me gustaría recibir comunicaciones sobre este espacio y similares.{' '}
                      <a
                        href="/politica-de-privacidad"
                        className="underline hover:text-primary"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver política de privacidad
                      </a>
                    </>
                  ),
                }}
                buttonProps={{
                  ...getInputProps(fields.consent, { type: 'checkbox' }),
                  name: 'consent',
                  form: form.id,
                  disabled: isSuccess,
                }}
                errors={fields.consent.errors}
              />
            </div>
          )}
          <Button className="w-full" size="lg" disabled={isSubmitting || isSuccess}>
            {isSuccess ? (
                <span>Enviado</span>
              ) : (
                <>
                  Contactar
                  <ExternalLink className="ml-2 h-4 w-4" />
                </>
              )}
          </Button>
        </div>
      </contactForm.Form>
        <p className="text-sm text-center text-muted-foreground">
          {isSuccess 
            ? "Gracias, tu mensaje ha sido enviado correctamente."
            : "Contacta ahora, habla directamente con el propietario"}
        </p>
    </div>
  );
}
